import createAttendeeConstraints from '../utils/createAttendeeConstraints';
import createLocationConstraints from '../utils/createLocationConstraint';
import createQueryConstraints from '../utils/createQueryConstraints';
import createTimeConstraints from '../utils/createTimeConstraints';
import FindMeetingLocationsRequest from 'owa-calendar-scheduling-service/lib/schema/FindMeetingLocationsRequest';
import PlacesSource from 'owa-service/lib/contract/PlacesSource';
import TimeSlot from 'owa-service/lib/contract/TimeSlot';
import { makePostRequest } from 'owa-ows-gateway';
import sessionStore from 'owa-session-store/lib/store/store';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';

const DEFAULT_MAX_RESULTS = 5;
const FML_ENDPOINT_URL = 'ows/Scheduling/api/v1.0/me/findmeetinglocations';
const APPNAME_HEADERNAME = 'x-findmeetinglocations-appname';
const APPNAME_HEADERVALUE = 'owa-react';
const SCENARIONAME_HEADERNAME = 'x-findmeetinglocations-appscenario';
const SCENARIONAME_HEADERVALUE = 'LocationWell';

/**
 * Generate FindMeetingLocationsRequest
 * @param queryString: The string to search
 * @param source: The places source to search
 * @param requiredAttendees: the required attendees, used by the ranker for more relevant results
 * @param optionalAttendees: the optional attendees, used by the ranker for more relevant results
 * @param maxResults: the max number of results requested by the caller
 * @param meetingTimeSlot: the meeting time, used by the ranker for more relevant results
 * @param coordinates: the client's geo-coordinates, used by bing for more relevant results
 * @param placeId: id for specified location
 * @param availableConferenceRoomsOnly: boolean indicating if we should fetch only available conference rooms
 * @param requestRoomOnly: boolean indicating if request is for prefetching location suggestions
 * @param recurrence: Event's recurrence
 */
function findMeetingLocationsRequestData(
    queryString: string,
    source: PlacesSource,
    requiredAttendees: string[],
    optionalAttendees: string[],
    maxResults: number,
    meetingTimeSlot: TimeSlot,
    coordinates: Coordinates,
    placeId: string,
    availableConferenceRoomsOnly?: boolean,
    requestRoomOnly?: boolean,
    recurrence?: RecurrenceType
): FindMeetingLocationsRequest {
    let request = <FindMeetingLocationsRequest>{};

    request.locationProvider = source;

    request.attendees = createAttendeeConstraints(requiredAttendees, optionalAttendees);

    request.timeConstraint = createTimeConstraints(source, meetingTimeSlot, recurrence);

    if (request.timeConstraint) {
        request.resolveLocationsAvailability = true;
    }

    // Request "AvailableConferenceRoomsOnly" only if availability is requested
    let requestAvailableConferenceRoomsOnly =
        availableConferenceRoomsOnly && request.resolveLocationsAvailability;

    request.queryConstraint = createQueryConstraints(
        queryString,
        placeId,
        request.resolveLocationsAvailability,
        requestAvailableConferenceRoomsOnly,
        requestRoomOnly
    );

    request.meetingDuration = meetingTimeSlot ? 'PT' + meetingTimeSlot.DurationInMinutes + 'M' : '';
    request.locationConstraint = createLocationConstraints(coordinates);
    request.bingMarket = sessionStore.userConfiguration.SessionSettings.UserCulture;

    request.maxCandidates = maxResults == 0 ? DEFAULT_MAX_RESULTS : maxResults;

    return request;
}

/**
 * Performs findMeetingLocations request
 *
 * @param queryString: The string to search
 * @param source: The places source to search
 * @param requiredAttendees: the required attendees, used by the ranker for more relevant results
 * @param optionalAttendeesL the optional attendees, used by the ranker for more relevant results
 * @param maxResults: the max number of results requested by the caller
 * @param meetingTimeSlot: the meeting time, used by the ranker for more relevant results
 * @param coordinates: the client's geo-coordinates, used by bing for more relevant results
 * @param placeId: id for specified location
 * @param {boolean} isPrefetch: boolean indicating if request is for prefetching location suggestions
 * @param {boolean} requestRoomsOnly: boolean indicating if request should return only conference rooms
 * @param recurrence: Event's recurrence
 */
export function findMeetingLocations(
    queryString: string,
    source: PlacesSource,
    requiredAttendees: string[] = null,
    optionalAttendees: string[] = null,
    maxResults: number = null,
    meetingTimeSlot: TimeSlot = null,
    coordinates: Coordinates = null,
    placeId: string = null,
    availableConferenceRoomsOnly?: boolean,
    requestRoomsOnly?: boolean,
    recurrence?: RecurrenceType
): Promise<Response> {
    let request = findMeetingLocationsRequestData(
        queryString,
        source,
        requiredAttendees,
        optionalAttendees,
        maxResults,
        meetingTimeSlot,
        coordinates,
        placeId,
        availableConferenceRoomsOnly,
        requestRoomsOnly,
        recurrence
    );

    const headers = {};
    headers[APPNAME_HEADERNAME] = APPNAME_HEADERVALUE;
    headers[SCENARIONAME_HEADERNAME] = SCENARIONAME_HEADERVALUE;

    return makePostRequest(
        FML_ENDPOINT_URL,
        request,
        undefined,
        true,
        headers,
        undefined,
        undefined,
        undefined,
        'findMeetingLocations'
    );
}
